// P3.1 — WebSocket realtime client (chat + notifications).
//
// Single shared WS connection per authenticated session, exposed via React
// context. Components subscribe to incoming-message events with `useRealtime()`
// and can send outbound actions with the `send` function it returns.
//
// Wire format (mirrors backend RealtimeWebSocket stack):
//   Client -> Server: { action: "sendMessage" | "markRead", ... }
//   Server -> Client: { type: "chat:new" | "notification:new" | "notification:read", ... }
//
// Behaviour:
//   - Connects only when a JWT is present in localStorage.
//   - Reconnects on close with exponential backoff capped at 30s.
//   - Sends a ping every 9 minutes to stay under the 10-minute idle
//     disconnect imposed by API Gateway WebSocket APIs.
//   - Buffers outbound messages while the socket is reconnecting and
//     flushes them on next open.
//   - React Query cache invalidation/updates are handled at the call site
//     (subscribe via `addListener`) so that this hook stays UI-agnostic.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const WS_URL = "wss://h14zwpvpac.execute-api.us-east-2.amazonaws.com/prod";
const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 9 * 60 * 1000; // 9 min < 10 min API GW idle timeout

const RealtimeContext = createContext({
  connected: false,
  send: () => false,
  addListener: () => () => {},
});

export const RealtimeProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const outboxRef = useRef([]);
  const backoffRef = useRef(1_000);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const shouldRunRef = useRef(true);

  const flushOutbox = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const queue = outboxRef.current;
    outboxRef.current = [];
    queue.forEach((payload) => {
      try {
        ws.send(payload);
      } catch {
        // requeue and let next open retry
        outboxRef.current.push(payload);
      }
    });
  }, []);

  const connect = useCallback(() => {
    if (!shouldRunRef.current) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const url = `${WS_URL}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      backoffRef.current = 1_000;
      flushOutbox();
      // start heartbeat
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      pingTimerRef.current = setInterval(() => {
        try {
          ws.send(JSON.stringify({ action: "ping" }));
        } catch {
          /* socket may have closed between checks */
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      let envelope;
      try {
        envelope = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!envelope || typeof envelope !== "object") return;
      listenersRef.current.forEach((fn) => {
        try {
          fn(envelope);
        } catch (err) {
          // listener bug should not break socket
          console.warn("realtime listener error:", err?.message);
        }
      });
    };

    ws.onerror = () => {
      // onclose will fire next; nothing else to do here
    };

    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (!shouldRunRef.current) return;
      const delay = Math.min(backoffRef.current, MAX_BACKOFF_MS);
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
  }, [flushOutbox]);

  useEffect(() => {
    shouldRunRef.current = true;
    connect();
    return () => {
      shouldRunRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      const ws = wsRef.current;
      if (ws && ws.readyState <= WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    };
  }, [connect]);

  const send = useCallback((payload) => {
    const text = JSON.stringify(payload);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(text);
        return true;
      } catch {
        outboxRef.current.push(text);
        return false;
      }
    }
    // buffer until next open (cap to avoid unbounded growth)
    if (outboxRef.current.length < 50) outboxRef.current.push(text);
    return false;
  }, []);

  const addListener = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  const value = useMemo(
    () => ({ connected, send, addListener }),
    [connected, send, addListener]
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);

// Subscribe helper: call `fn(envelope)` for every incoming message of `type`.
// Pass an array of types to subscribe to multiple. Returns an unsubscribe fn.
export const useRealtimeSubscription = (types, fn) => {
  const { addListener } = useRealtime();
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const wanted = Array.isArray(types) ? types : [types];
    const off = addListener((envelope) => {
      if (wanted.includes(envelope?.type)) fnRef.current(envelope);
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addListener, Array.isArray(types) ? types.join("|") : types]);
};
